import { useEffect, useState, useMemo, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Clock, ArrowLeft, ArrowRight, ChevronRight, Linkedin, Twitter, Facebook, Sun, Moon, Type, BookOpenCheck } from "lucide-react";
import { getArticleBySlug, getRelatedArticles } from "@/data/blogArticles";
import { supabase } from "@/integrations/supabase/client";
import { track } from "@/lib/track";
import { Helmet } from "react-helmet-async";
import type { BlogArticle as BlogArticleType } from "@/data/blogArticles";

type Mode = "dark" | "light";

const palettes: Record<Mode, Record<string, string>> = {
  dark: {
    bg: "hsl(0 0% 4%)",
    bgGradient: "radial-gradient(1200px 600px at 50% -10%, hsl(207 68% 50% / 0.10), transparent 60%)",
    surface: "hsl(0 0% 7%)",
    surfaceAlt: "hsl(0 0% 8%)",
    border: "hsl(0 0% 14%)",
    borderSoft: "hsl(0 0% 12%)",
    text: "hsl(0 0% 95%)",
    textStrong: "hsl(0 0% 98%)",
    textBody: "hsl(0 0% 78%)",
    textMuted: "hsl(0 0% 65%)",
    accent: "hsl(207 68% 55%)",
    accentSoft: "hsl(207 68% 50% / 0.12)",
    accentText: "hsl(207 68% 72%)",
    chipBg: "hsl(207 68% 50% / 0.15)",
    progressTrack: "hsl(0 0% 10%)",
    hoverBg: "rgba(255,255,255,0.06)",
    quoteRule: "hsl(207 68% 55%)",
  },
  light: {
    bg: "hsl(40 30% 98%)",
    bgGradient: "radial-gradient(1200px 600px at 50% -10%, hsl(207 68% 50% / 0.08), transparent 60%)",
    surface: "hsl(40 25% 96%)",
    surfaceAlt: "hsl(0 0% 100%)",
    border: "hsl(30 15% 88%)",
    borderSoft: "hsl(30 15% 92%)",
    text: "hsl(220 25% 12%)",
    textStrong: "hsl(220 30% 8%)",
    textBody: "hsl(220 15% 28%)",
    textMuted: "hsl(220 10% 45%)",
    accent: "hsl(218 60% 30%)",
    accentSoft: "hsl(218 60% 30% / 0.08)",
    accentText: "hsl(218 60% 25%)",
    chipBg: "hsl(218 60% 30% / 0.10)",
    progressTrack: "hsl(30 15% 90%)",
    hoverBg: "rgba(0,0,0,0.04)",
    quoteRule: "hsl(42 62% 50%)",
  },
};

const fontSizes = { sm: 16, md: 18, lg: 20 } as const;
type FontSize = keyof typeof fontSizes;

const BlogArticle = () => {
  const { slug } = useParams<{ slug: string }>();
  const [progress, setProgress] = useState(0);
  const progressRef = useRef(0);
  const [dbArticle, setDbArticle] = useState<BlogArticleType | null>(null);
  const [dbRelated, setDbRelated] = useState<BlogArticleType[]>([]);
  const [loadedFromDb, setLoadedFromDb] = useState(false);
  const [mode, setMode] = useState<Mode>(() => {
    if (typeof window === "undefined") return "dark";
    return (localStorage.getItem("blog-read-mode") as Mode) || "dark";
  });
  const [fontSize, setFontSize] = useState<FontSize>(() => {
    if (typeof window === "undefined") return "md";
    return (localStorage.getItem("blog-read-size") as FontSize) || "md";
  });

  useEffect(() => { localStorage.setItem("blog-read-mode", mode); }, [mode]);
  useEffect(() => { localStorage.setItem("blog-read-size", fontSize); }, [fontSize]);
  const [activeSection, setActiveSection] = useState<string | null>(null);

  const c = palettes[mode];
  const bodyPx = fontSizes[fontSize];

  useEffect(() => {
    const fetchFromDb = async () => {
      const { data } = await supabase
        .from("blog_articles").select("*").eq("slug", slug || "").eq("published", true).maybeSingle();
      if (data) {
        const mapped: BlogArticleType = {
          slug: data.slug, title: data.title, excerpt: data.excerpt, summary: data.summary,
          category: data.category as any, author: data.author, authorRole: data.author_role,
          date: data.date, readingTime: data.reading_time, featured: data.featured,
          content: (data.content as any) || [], keyTakeaways: (data.key_takeaways as any) || [],
          faqs: (data.faqs as any) || [], relatedSlugs: (data.related_slugs as any) || [],
          seoTitle: data.seo_title, metaDescription: data.meta_description,
        };
        setDbArticle(mapped);
        if (mapped.relatedSlugs.length > 0) {
          const { data: relData } = await supabase
            .from("blog_articles").select("slug, title, excerpt, category")
            .in("slug", mapped.relatedSlugs).eq("published", true);
          if (relData) setDbRelated(relData as any);
        }
      }
      setLoadedFromDb(true);
    };
    fetchFromDb();
  }, [slug]);

  const article = useMemo(() => dbArticle || getArticleBySlug(slug || ""), [dbArticle, slug, loadedFromDb]);
  const related = useMemo(() => {
    if (dbArticle && dbRelated.length > 0) return dbRelated;
    if (article) return getRelatedArticles(article);
    return [];
  }, [dbArticle, dbRelated, article]);

  const tocItems = useMemo(() => {
    let h2Count = 0;
    return (article?.content || []).map((section, i) => {
      if (section.level === "h2") h2Count += 1;
      return { id: `sec-${i}`, heading: section.heading, level: section.level, number: section.level === "h2" ? String(h2Count).padStart(2, "0") : null };
    });
  }, [article]);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  // Reading analytics: time on page (15s/30s/60s milestones + on leave) and completion.
  useEffect(() => {
    if (!slug) return;
    const startedAt = Date.now();
    const fired = new Set<number>();
    const sendRead = (completed: boolean) => {
      const secs = Math.round((Date.now() - startedAt) / 1000);
      track("article_read", {
        label: slug,
        metadata: { slug, time_on_page_s: secs, completed },
      });
    };
    const timers = [15, 30, 60].map((s) =>
      window.setTimeout(() => { if (!fired.has(s)) { fired.add(s); sendRead(false); } }, s * 1000)
    );
    const onLeave = () => sendRead(progressRef.current >= 90);
    window.addEventListener("pagehide", onLeave);
    return () => {
      timers.forEach((t) => window.clearTimeout(t));
      window.removeEventListener("pagehide", onLeave);
      sendRead(progressRef.current >= 90);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const pct = docHeight > 0 ? Math.min((scrollTop / docHeight) * 100, 100) : 0;
      setProgress(pct);
      progressRef.current = pct;

      let current: string | null = null;
      const len = article?.content?.length || 0;
      for (let i = 0; i < len; i++) {
        const el = document.getElementById(`sec-${i}`);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 140) {
            current = `sec-${i}`;
          }
        }
      }
      setActiveSection(current);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [article]);

  if (!article && loadedFromDb) {
    return (
      <div className="flex min-h-screen items-center justify-center" style={{ background: c.bg }}>
        <div className="text-center">
          <h1 className="mb-4 text-2xl font-bold" style={{ color: c.text }}>Article not found</h1>
          <Link to="/blog" className="text-sm underline" style={{ color: c.accent }}>Back to Blog</Link>
        </div>
      </div>
    );
  }
  if (!article) {
    return (
      <div className="flex min-h-screen items-center justify-center" style={{ background: c.bg }}>
        <p style={{ color: c.textMuted }}>Loading...</p>
      </div>
    );
  }

  const shareUrl = `https://www.elevateglobalsolutions.net/blog/${article.slug}`;
  const shareText = encodeURIComponent(article.title);

  const articleSchema = {
    "@context": "https://schema.org", "@type": "Article", headline: article.title,
    description: article.metaDescription, author: { "@type": "Organization", name: article.author },
    datePublished: article.date, publisher: { "@type": "Organization", name: "Elevate Global Solutions" },
  };
  const faqSchema = {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: article.faqs.map((faq) => ({
      "@type": "Question", name: faq.question,
      acceptedAnswer: { "@type": "Answer", text: faq.answer },
    })),
  };
  const breadcrumbSchema = {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://www.elevateglobalsolutions.net/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://www.elevateglobalsolutions.net/blog" },
      { "@type": "ListItem", position: 3, name: article.title },
    ],
  };

  // Section number counter for h2 only
  let h2Index = 0;

  const cycleSize = () => {
    setFontSize((s) => (s === "sm" ? "md" : s === "md" ? "lg" : "sm"));
  };

  return (
    <>
      <Helmet>
        <title>{article.seoTitle}</title>
        <meta name="description" content={article.metaDescription} />
        <meta property="og:title" content={article.seoTitle} />
        <meta property="og:description" content={article.metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://www.elevateglobalsolutions.net/blog/${article.slug}`} />
        <meta property="og:image" content="https://www.elevateglobalsolutions.net/og-default.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://www.elevateglobalsolutions.net/og-default.jpg" />
        <meta name="twitter:title" content={article.seoTitle} />
        <meta name="twitter:description" content={article.metaDescription} />
        <link rel="canonical" href={`https://www.elevateglobalsolutions.net/blog/${article.slug}`} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Reading Progress Bar (pinned) */}
      <div className="fixed left-0 top-0 z-50 h-1 w-full" style={{ background: c.progressTrack }}>
        <div className="h-full transition-[width] duration-100"
          style={{ width: `${progress}%`, background: c.accent }} />
      </div>

      {/* Pinned Reading Progress Pill */}
      <div
        className="fixed left-4 top-4 z-50 flex items-center gap-2 rounded-full px-3 py-1.5 shadow-md backdrop-blur md:left-6"
        style={{ background: c.cardBg, border: `1px solid ${c.border}`, color: c.text }}
        title={`Reading progress: ${Math.round(progress)}%`}
        aria-label={`Reading progress ${Math.round(progress)} percent`}
      >
        <svg width="22" height="22" viewBox="0 0 32 32" className="shrink-0">
          <circle cx="16" cy="16" r="13" fill="none" strokeWidth="3" stroke={c.progressTrack} />
          <circle
            cx="16" cy="16" r="13" fill="none" strokeWidth="3" stroke={c.accent}
            strokeLinecap="round"
            strokeDasharray={2 * Math.PI * 13}
            strokeDashoffset={2 * Math.PI * 13 * (1 - progress / 100)}
            transform="rotate(-90 16 16)"
            style={{ transition: "stroke-dashoffset 0.2s ease" }}
          />
        </svg>
        <span className="text-xs font-semibold tabular-nums">{Math.round(progress)}%</span>
      </div>

      {/* Floating Reader Controls */}
      <div className="fixed right-4 top-20 z-40 flex flex-col gap-2 md:right-6 md:top-24">
        <button
          onClick={() => setMode(mode === "dark" ? "light" : "dark")}
          aria-label={`Switch to ${mode === "dark" ? "light" : "dark"} mode`}
          title={`Switch to ${mode === "dark" ? "light" : "dark"} mode`}
          className="flex h-10 w-10 items-center justify-center rounded-full border shadow-lg backdrop-blur transition-transform hover:scale-105"
          style={{ background: c.surfaceAlt, borderColor: c.border, color: c.text }}>
          {mode === "dark" ? <Sun size={16} /> : <Moon size={16} />}
        </button>
        <button
          onClick={cycleSize}
          aria-label="Change text size"
          title={`Text size: ${fontSize.toUpperCase()}`}
          className="flex h-10 w-10 items-center justify-center rounded-full border shadow-lg backdrop-blur transition-transform hover:scale-105"
          style={{ background: c.surfaceAlt, borderColor: c.border, color: c.text }}>
          <Type size={fontSize === "sm" ? 13 : fontSize === "md" ? 16 : 19} />
        </button>
      </div>

      <div className="min-h-screen" style={{ background: c.bg, backgroundImage: c.bgGradient, backgroundRepeat: "no-repeat" }}>
        <article className="container-narrow px-4 pb-20 pt-20 md:px-8 md:pt-28 lg:px-16">
          {/* Breadcrumbs */}
          <nav aria-label="Breadcrumb" className="mb-8">
            <ol className="flex flex-wrap items-center gap-1.5 text-sm" style={{ color: c.textMuted }}>
              <li><Link to="/" className="transition-colors hover:opacity-80" style={{ color: c.textMuted }}>Home</Link></li>
              <li><ChevronRight size={14} /></li>
              <li><Link to="/blog" className="transition-colors hover:opacity-80" style={{ color: c.textMuted }}>Blog</Link></li>
              <li><ChevronRight size={14} /></li>
              <li className="max-w-[200px] truncate" style={{ color: c.text }}>{article.title}</li>
            </ol>
          </nav>

          {/* Header */}
          <header className="mb-12 max-w-3xl">
            <span className="mb-4 inline-block rounded-full px-3 py-1 text-xs font-medium uppercase tracking-wider"
              style={{ background: c.chipBg, color: c.accentText }}>
              {article.category}
            </span>
            <h1 className="mb-5 text-3xl font-bold leading-[1.1] tracking-tight md:text-5xl lg:text-6xl" style={{ color: c.textStrong }}>
              {article.title}
            </h1>
            <p className="mb-6 text-lg leading-relaxed md:text-xl" style={{ color: c.textBody }}>
              {article.excerpt}
            </p>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm" style={{ color: c.textMuted }}>
              <span style={{ color: c.text }}>{article.author}</span>
              <span>·</span>
              <span>{article.authorRole}</span>
              <span>·</span>
              <span>{new Date(article.date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
              <span>·</span>
              <span className="flex items-center gap-1"><Clock size={14} /> {article.readingTime} min read</span>
            </div>
            <div className="mt-6 flex items-center gap-3">
              <span className="text-xs uppercase tracking-wider" style={{ color: c.textMuted }}>Share</span>
              <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`} target="_blank" rel="noopener noreferrer" aria-label={`Share on LinkedIn`} className="rounded-full p-2 transition-colors" style={{ color: c.textMuted, background: "transparent" }} onMouseEnter={(e) => (e.currentTarget.style.background = c.hoverBg)} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}><Linkedin size={16} aria-hidden="true" /></a>
              <a href={`https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`} target="_blank" rel="noopener noreferrer" aria-label={`Share on Twitter`} className="rounded-full p-2 transition-colors" style={{ color: c.textMuted }} onMouseEnter={(e) => (e.currentTarget.style.background = c.hoverBg)} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}><Twitter size={16} aria-hidden="true" /></a>
              <a href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`} target="_blank" rel="noopener noreferrer" aria-label={`Share on Facebook`} className="rounded-full p-2 transition-colors" style={{ color: c.textMuted }} onMouseEnter={(e) => (e.currentTarget.style.background = c.hoverBg)} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}><Facebook size={16} aria-hidden="true" /></a>
            </div>
          </header>

          {/* Summary / TL;DR */}
          <section
            className="mb-12 max-w-3xl rounded-xl border-l-4 p-6 pl-7"
            style={{ background: c.surface, borderColor: c.quoteRule }}>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider" style={{ color: c.accentText }}>TL;DR</div>
            <p className="text-base leading-relaxed md:text-lg" style={{ color: c.textBody }}>{article.summary}</p>
          </section>

          {/* Table of Contents */}
          {tocItems.length > 0 && (
            <section className="mb-12 max-w-3xl rounded-xl border p-6" style={{ background: c.surfaceAlt, borderColor: c.border }}>
              <div className="mb-3 text-xs font-semibold uppercase tracking-wider" style={{ color: c.accentText }}>In this article</div>
              <ul className="space-y-2">
                {tocItems.map((item) => (
                  <li key={item.id} className="relative">
                    {activeSection === item.id && (
                      <span className="absolute left-0 top-1/2 h-4 w-0.5 -translate-y-1/2 rounded-full" style={{ background: c.accent }} />
                    )}
                    <a
                      href={`#${item.id}`}
                      className="block text-sm transition-colors hover:opacity-80"
                      style={{
                        color: activeSection === item.id ? c.accentText : c.textMuted,
                        paddingLeft: item.level === "h3" ? "1.25rem" : "0.75rem",
                        fontWeight: activeSection === item.id ? 600 : 400,
                      }}
                    >
                      {item.number ? `${item.number}  ` : ""}{item.heading}
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Content Sections */}
          <div className="mb-12 max-w-3xl space-y-12">
            {article.content.map((section, i) => {
              const isH2 = section.level === "h2";
              if (isH2) h2Index += 1;
              const sectionNumber = isH2 ? String(h2Index).padStart(2, "0") : null;
              const isFirstSection = i === 0;

              return (
                <section key={i} id={`sec-${i}`}>
                  {isH2 ? (
                    <div className="mb-5">
                      {sectionNumber && (
                        <div className="mb-2 flex items-center gap-3 text-xs font-mono uppercase tracking-widest" style={{ color: c.accentText }}>
                          <span>{sectionNumber}</span>
                          <span className="h-px flex-1" style={{ background: c.border }} />
                        </div>
                      )}
                      <h2 className="text-2xl font-bold leading-tight md:text-3xl" style={{ color: c.textStrong }}>
                        {section.heading}
                      </h2>
                    </div>
                  ) : (
                    <h3 className="mb-3 text-lg font-semibold md:text-xl" style={{ color: c.text }}>
                      {section.heading}
                    </h3>
                  )}
                  {section.paragraphs.map((p, j) => {
                    const isLead = isFirstSection && j === 0;
                    return (
                      <p
                        key={j}
                        className="mb-5 leading-[1.8]"
                        style={{
                          color: c.textBody,
                          fontSize: `${bodyPx}px`,
                        }}>
                        {isLead ? (
                          <>
                            <span
                              className="float-left mr-2 mt-1 font-display font-bold leading-none"
                              style={{ fontSize: `${bodyPx * 3.2}px`, color: c.textStrong }}>
                              {p.charAt(0)}
                            </span>
                            {p.slice(1)}
                          </>
                        ) : (
                          p
                        )}
                      </p>
                    );
                  })}
                  {/* Pull quote, every 2nd h2 section, derived from key takeaway */}
                  {isH2 && h2Index % 2 === 0 && article.keyTakeaways[Math.floor(h2Index / 2) - 1] && (
                    <blockquote
                      className="my-8 border-l-4 pl-6 text-xl font-medium italic leading-snug md:text-2xl"
                      style={{ borderColor: c.quoteRule, color: c.textStrong }}>
                      "{article.keyTakeaways[Math.floor(h2Index / 2) - 1]}"
                    </blockquote>
                  )}
                </section>
              );
            })}
          </div>

          {/* Key Takeaways */}
          <section className="mb-12 max-w-3xl rounded-xl border p-6"
            style={{ background: c.accentSoft, borderColor: c.border }}>
            <h2 className="mb-4 text-xl font-bold" style={{ color: c.textStrong }}>Key Takeaways</h2>
            <ul className="space-y-3">
              {article.keyTakeaways.map((t, i) => (
                <li key={i} className="flex items-start gap-3 text-base" style={{ color: c.textBody }}>
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: c.accent }} />
                  {t}
                </li>
              ))}
            </ul>
          </section>

          {/* FAQ */}
          <section className="mb-12 max-w-3xl">
            <h2 className="mb-6 text-xl font-bold" style={{ color: c.textStrong }}>Frequently Asked Questions</h2>
            <div className="space-y-4">
              {article.faqs.map((faq, i) => (
                <div key={i} className="rounded-xl border p-5" style={{ background: c.surface, borderColor: c.border }}>
                  <h3 className="mb-2 text-base font-semibold" style={{ color: c.text }}>{faq.question}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: c.textBody }}>{faq.answer}</p>
                </div>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="mb-12 max-w-3xl rounded-xl border p-8 text-center"
            style={{ background: c.accentSoft, borderColor: c.border }}>
            <h2 className="mb-3 text-2xl font-bold" style={{ color: c.textStrong }}>Ready to Elevate Your Business?</h2>
            <p className="mb-6 text-base" style={{ color: c.textBody }}>
              Whether you need lead generation, cold callers, sales coaching, or a professional website, we've got you covered. Let's talk about what your business needs to grow.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/book" className="inline-flex items-center gap-2 rounded-lg px-6 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90" style={{ background: c.accent }}>
                Book a Free Call <ArrowRight size={16} />
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-2 rounded-lg border px-6 py-3 text-sm font-semibold transition-colors" style={{ borderColor: c.border, color: c.text }}>
                Contact Us
              </Link>
            </div>
          </section>

          {/* GoHighLevel CTA */}
          <section className="mb-12 max-w-3xl rounded-xl border p-6 text-center" style={{ background: c.surface, borderColor: c.border }}>
            <h3 className="mb-2 text-lg font-bold" style={{ color: c.text }}>Need a CRM That Does It All?</h3>
            <p className="mb-4 text-sm leading-relaxed" style={{ color: c.textBody }}>
              We set our clients up on GoHighLevel, the all-in-one platform for managing leads, automating follow-ups, booking appointments, and closing more deals.
            </p>
            <a href="https://gohighlevel.com/annual?fp_ref=elevate-global-solutions" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-lg px-6 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90" style={{ background: "hsl(150 60% 40%)" }}>
              Try GoHighLevel <ArrowRight size={16} />
            </a>
          </section>

          {/* Back */}
          <div className="mb-16 max-w-3xl">
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-medium transition-all hover:gap-3" style={{ color: c.accent }}>
              <ArrowLeft size={16} /> Back to all articles
            </Link>
          </div>

          {/* Related */}
          {related.length > 0 && (
            <section className="max-w-3xl border-t pt-12" style={{ borderColor: c.borderSoft }}>
              <h2 className="mb-6 text-xl font-bold" style={{ color: c.textStrong }}>Related Articles</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                {related.map((r: any) => (
                  <Link key={r.slug} to={`/blog/${r.slug}`}
                    className="group rounded-xl border p-5 transition-all duration-300"
                    style={{ background: c.surfaceAlt, borderColor: c.border }}>
                    <span className="mb-2 inline-block text-xs font-medium uppercase tracking-wider" style={{ color: c.accentText }}>{r.category}</span>
                    <h3 className="mb-2 text-base font-semibold" style={{ color: c.text }}>{r.title}</h3>
                    <p className="text-sm" style={{ color: c.textBody }}>{r.excerpt}</p>
                    <div className="mt-3 flex items-center gap-1.5 text-sm font-medium transition-all group-hover:gap-2.5" style={{ color: c.accent }}>
                      Read more <ArrowRight size={14} aria-hidden="true" />
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </div>
    </>
  );
};

export default BlogArticle;
